import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Calendar,
  ChevronDown,
  ChevronUp,
  RotateCcw,
} from "lucide-react";
import { journalEntriesApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import { PeriodKey, periodOptions, getDateRange } from "./periodPresets";

interface AccountLedgerModalProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType?: string;
}

interface JournalLine {
  accountId: { _id: string; code: string; name: string; type: string } | string;
  debitCents: number;
  creditCents: number;
  memo?: string | null;
}

interface JournalEntry {
  _id: string;
  postedAt: string;
  memo: string | null;
  source: { type: string; id: string };
  lines: JournalLine[];
  reversedByEntryId?: string | null;
  postedBy?: { name?: string; email?: string } | null;
}

function formatCents(cents: number): string {
  if (cents === 0) return "-";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const sourceLabels: Record<string, string> = {
  trust_receipt: "Trust Receipt",
  trust_disbursement: "Trust Disbursement",
  invoice: "Invoice",
  payment: "Payment",
  transfer: "Transfer",
  correction: "Correction",
  manual: "Manual",
};

const sourceBadge: Record<string, { bg: string; text: string }> = {
  trust_receipt: { bg: "bg-emerald-100", text: "text-emerald-700" },
  trust_disbursement: { bg: "bg-blue-100", text: "text-blue-700" },
  invoice: { bg: "bg-amber-100", text: "text-amber-700" },
  payment: { bg: "bg-purple-100", text: "text-purple-700" },
  transfer: { bg: "bg-slate-100", text: "text-slate-700" },
  correction: { bg: "bg-red-100", text: "text-red-700" },
  manual: { bg: "bg-slate-100", text: "text-slate-700" },
};

const LIMIT = 25;

const AccountLedgerModal: React.FC<AccountLedgerModalProps> = ({
  open,
  onClose,
  accountId,
  accountCode,
  accountName,
  accountType,
}) => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [period, setPeriod] = useState<PeriodKey>("all-time");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const allPeriodOptions = [
    { key: "all-time" as PeriodKey, label: "All Time" },
    ...periodOptions,
  ];

  const handlePeriodChange = (key: PeriodKey) => {
    setPeriod(key);
    if (key === "all-time") {
      setDateFrom("");
      setDateTo("");
      setPage(1);
      return;
    }
    if (key === "custom") return;
    const range = getDateRange(key);
    if (range) {
      setDateFrom(range.from);
      setDateTo(range.to);
      setPage(1);
    }
  };

  const loadEntries = useCallback(async () => {
    if (!accountId || !open) return;
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page,
        limit: LIMIT,
        accountId,
      };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await journalEntriesApi.list(params);
      setEntries(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to load account ledger:", err);
    } finally {
      setLoading(false);
    }
  }, [accountId, open, page, dateFrom, dateTo]);

  useEffect(() => {
    if (open) {
      setPage(1);
      setExpandedId(null);
      setPeriod("all-time");
      setDateFrom("");
      setDateTo("");
    }
  }, [open, accountId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const totalPages = Math.ceil(total / LIMIT);

  // Calculate running balance from entries
  // For debit-normal accounts (asset, expense): debit increases, credit decreases
  // For credit-normal accounts (liability, equity, income): credit increases, debit decreases
  const isDebitNormal = accountType === "asset" || accountType === "expense";

  // Extract only this account's lines from each entry
  const rows = entries.map((entry) => {
    let debitCents = 0;
    let creditCents = 0;
    entry.lines.forEach((line) => {
      const lineAcctId = typeof line.accountId === "object" ? line.accountId._id : line.accountId;
      if (lineAcctId === accountId) {
        debitCents += line.debitCents || 0;
        creditCents += line.creditCents || 0;
      }
    });
    return { entry, debitCents, creditCents };
  });

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title={`${accountCode} - ${accountName}`}
      icon={BookOpen}
      size="xl"
    >
      {/* Period presets + date filters */}
      <div className="mb-4 space-y-2.5">
        {/* Period preset pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          {allPeriodOptions.map((p) => (
            <button
              key={p.key}
              onClick={() => handlePeriodChange(p.key)}
              className={`px-2.5 py-1 rounded-xl text-xs font-medium transition-colors ${
                period === p.key
                  ? "bg-brand text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {p.label}
            </button>
          ))}
          <p className="ml-auto text-xs text-slate-500">
            {total} {total === 1 ? "entry" : "entries"}
          </p>
        </div>

        {/* Date inputs (shown when custom or any preset — always visible for context) */}
        {period !== "all-time" && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPeriod("custom"); setPage(1); }}
                  className="px-2.5 py-1.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPeriod("custom"); setPage(1); }}
                  className="px-2.5 py-1.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm font-medium text-slate-500">No journal entries for this account</p>
          <p className="text-xs text-slate-400 mt-1">Transactions will appear here as they are posted</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">Date</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">Memo</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">Source</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">Debit</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">Credit</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">
                    Balance ({isDebitNormal ? "DR" : "CR"})
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(() => {
                  let runningBalance = 0;
                  return rows.map(({ entry, debitCents, creditCents }) => {
                    if (isDebitNormal) {
                      runningBalance += debitCents - creditCents;
                    } else {
                      runningBalance += creditCents - debitCents;
                    }
                    const badge = sourceBadge[entry.source.type] || sourceBadge.manual;
                    const isExpanded = expandedId === entry._id;

                    return (
                      <React.Fragment key={entry._id}>
                        <tr
                          className={`hover:bg-slate-50 cursor-pointer ${entry.reversedByEntryId ? "opacity-50" : ""}`}
                          onClick={() => setExpandedId(isExpanded ? null : entry._id)}
                        >
                          <td className="px-4 py-2 text-slate-700 whitespace-nowrap text-xs">
                            <div className="flex items-center gap-1">
                              {isExpanded
                                ? <ChevronUp className="h-3 w-3 text-slate-400" />
                                : <ChevronDown className="h-3 w-3 text-slate-400" />}
                              {formatDate(entry.postedAt)}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-slate-800 max-w-[200px] truncate text-xs" title={entry.memo || ""}>
                            {entry.memo || "-"}
                            {entry.reversedByEntryId && (
                              <span className="ml-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] bg-red-50 text-red-600">
                                <RotateCcw className="h-2.5 w-2.5" /> Reversed
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${badge.bg} ${badge.text}`}>
                              {sourceLabels[entry.source.type] || entry.source.type}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-xs text-slate-800">
                            {formatCents(debitCents)}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-xs text-slate-800">
                            {formatCents(creditCents)}
                          </td>
                          <td className={`px-4 py-2 text-right font-mono text-xs font-semibold ${
                            runningBalance < 0 ? "text-red-600" : "text-slate-800"
                          }`}>
                            {formatCents(Math.abs(runningBalance))}
                            {runningBalance < 0 && (
                              <span className="text-[10px] ml-0.5">{isDebitNormal ? "CR" : "DR"}</span>
                            )}
                          </td>
                        </tr>
                        {/* Expanded: show all lines in this entry */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="p-0">
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                className="bg-slate-50 border-t border-slate-200 px-4 py-2"
                              >
                                <p className="text-[10px] font-medium text-slate-500 mb-1 uppercase tracking-wider">
                                  Full Entry Lines
                                </p>
                                <table className="w-full text-xs">
                                  <tbody className="divide-y divide-slate-100">
                                    {entry.lines.map((line, li) => {
                                      const acct = typeof line.accountId === "object" ? line.accountId : null;
                                      const isThis = acct?._id === accountId;
                                      return (
                                        <tr key={li} className={isThis ? "bg-emerald-50/50" : ""}>
                                          <td className="py-1 pr-3 font-mono text-slate-500 w-14">
                                            {acct?.code || "-"}
                                          </td>
                                          <td className={`py-1 pr-3 ${isThis ? "font-semibold text-emerald-800" : "text-slate-700"}`}>
                                            {acct?.name || "Unknown"}
                                            {isThis && <span className="text-[10px] ml-1 text-emerald-600">(this account)</span>}
                                          </td>
                                          <td className="py-1 text-right font-mono text-slate-700 w-24">
                                            {formatCents(line.debitCents || 0)}
                                          </td>
                                          <td className="py-1 text-right font-mono text-slate-700 w-24">
                                            {formatCents(line.creditCents || 0)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50">
              <p className="text-xs text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="p-1 rounded-xl border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-40"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="p-1 rounded-xl border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-40"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </PremiumModal>
  );
};

export default AccountLedgerModal;
